(function (root) {
  'use strict';
  const catalog = root.ThreeOneCatalog;
  const products = new Map(catalog.products.map(p => [p.key, p]));
  const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  // Keep full-resolution masters in the catalog; request only tile-sized CDN renditions.
  function imageUrl(source, width) {
    try {
      const url = new URL(source);
      if (url.hostname !== 'cdn.shopify.com') return source;
      url.searchParams.set('width', width);
      return url.href;
    } catch (_) { return source; }
  }
  function tile(item, quantity = 1) {
    const label = item.label || item.title;
    return `<li class="ThreeOneTile">${item.image ? `<img src="${escape(imageUrl(item.image, 320))}" srcset="${[160,320,640].map(w => `${escape(imageUrl(item.image, w))} ${w}w`).join(', ')}" sizes="(max-width: 640px) 45vw, 240px" alt="${escape(label)}" width="160" height="160" loading="lazy" decoding="async">` : '<span class="ThreeOneTile__Missing" aria-hidden="true">Photo pending</span>'}<span>${quantity} × ${escape(label)}</span>${item.note ? `<small>${escape(item.note)}</small>` : ''}</li>`;
  }
  function inclusions(key, quantity = 1) {
    const p = products.get(key);
    if (!p || !p.includes.length) return '';
    return `<div class="ThreeOneContents"><p class="ThreeOneEyebrow">Included${quantity > 1 ? ` with your ${quantity} items` : ''}</p><ul class="ThreeOneTiles">${p.includes.map(i => tile(i, i.quantity * quantity)).join('')}</ul></div>`;
  }
  // A complete set is one stocked SKU. Displaying its contents never adds cart lines.
  // This packing projection does not create fulfillments or calculate shipping rates.
  function pack(lines) {
    const packages = [], unresolved = [], pins = [];
    const buckets = {curved: [], flat: [], slippers: []};
    for (const line of lines) {
      const product = products.get(line.key);
      const quantity = Number(line.quantity);
      if (!Number.isSafeInteger(quantity) || quantity < 1) continue;
      const item = {key: line.key, title: line.title || product?.title || 'Item', image: product?.image, quantity, lineId: line.lineId};
      if (!product) { unresolved.push(item); continue; }
      if (product.family === 'pin-pack') { pins.push(item); continue; }
      if (buckets[product.family]) { buckets[product.family].push(item); continue; }
      const family = product.family.replace('set-', '');
      for (let n = 0; n < quantity; n++) packages.push({family, title: product.title, separate: true, items: [{...item, quantity: 1}], packaging: catalog.packaging[family]?.includes || []});
    }
    const grouped = [];
    for (const [family, items] of Object.entries(buckets)) {
      const capacity = catalog.packaging[family].capacity;
      const remaining = items.map(i => ({...i}));
      const units = [];
      // Round-robin keeps two of each style distributed across two four-hat boxes.
      while (remaining.some(i => i.quantity > 0)) {
        for (const item of remaining) if (item.quantity > 0) { units.push({...item, quantity: 1}); item.quantity--; }
      }
      for (let offset = 0; offset < units.length; offset += capacity) {
        const selected = units.slice(offset, offset + capacity), merged = [];
        for (const unit of selected) {
          const match = merged.find(i => i.key === unit.key && i.lineId === unit.lineId);
          if (match) match.quantity++; else merged.push({...unit});
        }
        grouped.push({family, capacity, title: catalog.packaging[family].title, separate: false, items: merged, packaging: catalog.packaging[family].includes});
      }
    }
    const packed = [...grouped, ...packages];
    if (pins.length) {
      const shared = packed.find(pkg => pkg.family !== 'mugs');
      if (shared) shared.items.push(...pins);
      else packed.push({family:'pin-pack', title:'3ONE HAT PINS', separate:true, items:pins, packaging:[]});
    }
    return {packages: packed, unresolved};
  }
  function packageContents(pkg) {
    const contents = [];
    const add = (item, quantity) => {
      const label = item.label || item.title;
      const existing = contents.find(i => i.label === label && i.image === item.image);
      if (existing) existing.quantity += quantity;
      else contents.push({...item,label,quantity});
    };
    for (const item of pkg.items) {
      const product = products.get(item.key);
      if (!product.family.startsWith('set-') && product.family !== 'mugs') add(item,item.quantity);
    }
    for (const item of pkg.items) {
      const product = products.get(item.key);
      for (const included of product.includes) add(included,included.quantity*item.quantity);
    }
    // Complete sets also list their shared certificate in the product contents.
    // Add each shared packing item once, regardless of the number of hats.
    for (const item of pkg.packaging) {
      if (!/box/i.test(item.label) && !contents.some(i => i.label === item.label)) add(item,item.quantity);
    }
    return contents;
  }
  function packageMarkup(lines) {
    const result = pack(lines);
    if (!result.packages.length && !result.unresolved.length) return '';
    const heading = result.unresolved.length ? 'How your order is packed' : `${result.packages.length} separate package${result.packages.length === 1 ? '' : 's'}`;
    const families = new Set(lines.filter(line => Number(line.quantity) > 0).map(line => products.get(line.key)?.family));
    const guidance = [];
    if (families.has('curved') || families.has('flat')) guidance.push('Up to four hats of the same brim type per box.');
    if (families.has('slippers')) guidance.push('Up to two slipper pairs per box, with a dust bag for each pair.');
    if (families.has('set-curved') || families.has('set-flat')) guidance.push('Each complete set ships separately.');
    if (families.has('chain')) guidance.push('Each chain ships separately.');
    if (families.has('mugs')) guidance.push('Each mug set ships in its own fitted packaging.');
    const packageBody = pkg => {
      const boxes = pkg.packaging.filter(i=>/box/i.test(i.label));
      return `${boxes.length ? `<div class="ThreeOneBoxes"><p class="ThreeOneEyebrow">Your boxes · included</p><ul class="ThreeOneTiles ThreeOneTiles--Boxes">${boxes.map(i=>tile(i,i.quantity)).join('')}</ul></div>` : ''}<div class="ThreeOnePackageContents"><p class="ThreeOneEyebrow">Everything inside this package</p><ul class="ThreeOneTiles ThreeOneTiles--Contents">${packageContents(pkg).map(i=>tile(i,i.quantity)).join('')}</ul></div>${pkg.family === 'curved' || pkg.family === 'flat' ? '<p class="ThreeOneNote">One order certificate and one dust bag are shared by the hats in this box.</p>' : ''}${pkg.family === 'mugs' ? '<p class="ThreeOneNote">Packed in its own fitted shipping packaging.</p>' : ''}`;
    };
    return `<section class="ThreeOnePacking">
      <div class="ThreeOnePacking__Heading"><p class="ThreeOneEyebrow">Packed for you</p><h3>${heading}</h3>${guidance.length ? `<p>${guidance.join(' ')}</p>` : ''}</div>
      ${result.packages.map((p, index) => `<details class="ThreeOnePackage" open><summary><span>Package ${String(index + 1).padStart(2, '0')}<strong>${escape(p.title)}</strong></span><span class="ThreeOneBadge">${p.separate ? 'Ships separately' : p.items.filter(i => products.get(i.key).family !== 'pin-pack').reduce((sum, i) => sum + i.quantity, 0) + ' / ' + p.capacity + (p.family === 'slippers' ? ' pairs' : ' hats')}</span></summary><div class="ThreeOnePackage__Body">${packageBody(p)}${p.items.some(i => products.get(i.key).family === 'pin-pack') ? '<p class="ThreeOneNote">Pins ship free. They share an available non-mug package, or ship separately when needed.</p>' : ''}</div></details>`).join('')}
      ${result.unresolved.length ? `<p class="ThreeOneNote">Packing for ${result.unresolved.map(i => escape(i.title)).join(', ')} will be confirmed before launch.</p>` : ''}</section>`;
  }
  // Reuse the Mexico delivery details saved on the cart in Shopify's checkout.
  function checkoutUrl(base, countryCode, fields) {
    const params = new URLSearchParams();
    const address = (key, value) => { if (value) params.set(`checkout[shipping_address][${key}]`, value); };
    const field = name => String(fields.get(`attributes[Mexico ${name}]`) || '').trim();
    address('country', countryCode);
    if (countryCode === 'MX') {
      const name = field('Full Name').split(/\s+/);
      address('first_name', name.length > 1 ? name.shift() : '');
      address('last_name', name.join(' '));
      address('address1', field('Street'));
      address('address2', [field('Interior Number'), field('Colonia')].filter(Boolean).join(', '));
      address('city', field('City'));
      address('province', field('State'));
      address('zip', field('Postal Code'));
      address('phone', field('Phone Number'));
      address('company', field('Company'));
      if (field('Email')) params.set('checkout[email]', field('Email'));
    }
    return base + (params.size ? '?' + params.toString() : '');
  }
  // Enrich older carts too. Keep quantity and customer properties intact; these texts never change stock.
  async function ensureCheckout() {
    const base = (root.routes?.rootUrlWithoutSlash || '') + '/cart';
    const read = async response => { if (!response.ok) throw new Error('Unable to save included items. Please try again.'); return response.json(); };
    let cart = await fetch(base + '.js', {credentials:'same-origin', cache:'no-store'}).then(read);
    const pending = line => {
      const product = catalog.products.find(p => p.handle === line.handle);
      const properties = line.handle === '31-logo-long-sleeve'
        ? {Packaging:'Ships separately in a polymailer.'}
        : product?.checkoutProperties;
      return properties && Object.entries(properties).some(([key,value]) => line.properties?.[key] !== value) ? properties : null;
    };
    // Re-read the returned cart after each change: Shopify can merge matching lines and change their keys.
    const limit = cart.items.length + 1;
    for (let n = 0; n < limit; n++) {
      const line = cart.items.find(item => pending(item));
      if (!line) return cart;
      cart = await fetch(base + '/change.js', {
        method:'POST', credentials:'same-origin', headers:{'Content-Type':'application/json'},
        body:JSON.stringify({id:line.key, quantity:line.quantity, properties:{...line.properties, ...pending(line)}})
      }).then(read);
    }
    throw new Error('Included items could not be saved. Please refresh your bag.');
  }
  root.ThreeOne = {pack, products, tile, inclusions, packageContents, packageMarkup, escape, ensureCheckout, checkoutUrl};
  if (typeof module !== 'undefined') module.exports = root.ThreeOne;
  if (typeof customElements !== 'undefined' && !customElements.get('threeone-packages')) {
    customElements.define('threeone-packages', class extends HTMLElement {
      connectedCallback() {
        const data = this.querySelectorAll('[data-threeone-line]');
        if (!data.length) return;
        try { this.innerHTML = packageMarkup([...data].map(item => ({key:item.dataset.key,lineId:item.dataset.lineId,title:item.dataset.title,quantity:Number(item.dataset.quantity)}))); }
        catch (error) { console.warn('3ONE packaging display unavailable', error); }
      }
    });
  }
})(globalThis);
